import { useMemo, useCallback, Fragment } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { EmptyState } from '@components/empty-state';
import { IconMessages } from '@components/icons/messages';
import { filtersToExternalQuery, Table, TableView } from '@components/table';
import type { TableProps } from '@components/table';
import { useTableStore } from '@components/table/table-store';
import { useEvent } from '@hooks/use-event';
import { toQueryString } from '@utils/requests';

import { Message, useMessageCount, useMessageList } from 'api/messages';
import { updateMessagesListSettings, useMessagesListSettings } from 'api/users';
import { FilterHeader } from 'components/headers/filter-header';
import { Loading } from 'components/Loading';
import { useStore } from 'models/Provider';

import { useMessagesColumns } from './messages-columns';
import { mapKeyToSortKey, pageSize, useMessagesFilterState } from './messages-config';
import { MessageOptionsMenu } from './messages-options-column';
import { useMessagesTableFilterConfig } from './messages-table-filters';

export const Messages = () => {
  const { t } = useStore();

  const settingsQuery = useMessagesListSettings({
    keepPreviousData: true,
  });
  const tableView = settingsQuery.data;
  const client = useQueryClient();

  const handleTableViewChange = useCallback(
    (newTableView: TableView) => {
      client.setQueryData(['messages-list-settings'], newTableView);
      updateMessagesListSettings(newTableView);
    },
    [client]
  );

  const availableColumns = useMessagesColumns();
  const tableFilterConfig = useMessagesTableFilterConfig();
  const totalCountQuery = useMessageCount();
  const totalCount = totalCountQuery.data || 0;

  const filterState = useMessagesFilterState();
  const search = useMessagesFilterState((state) => state.debouncedSearch);

  const sort = tableView?.sortingState ? tableView.sortingState[0] : null;

  const queryString = useMemo(() => {
    const filterParams = filtersToExternalQuery(tableFilterConfig, tableView?.appliedFilters ?? []);

    return toQueryString({
      page_size: pageSize,
      ...filterParams,
      search: search || null,
      ordering: sort ? `${sort?.desc ? '-' : ''}${mapKeyToSortKey[sort.id] || sort?.id}` : '',
    });
  }, [search, sort, tableFilterConfig, tableView]);

  const messagesQuery = useMessageList(queryString, {
    enabled: settingsQuery.isSuccess,
    keepPreviousData: true,
  });

  const onPersist = useEvent((tableView: TableView) => {
    handleTableViewChange(tableView);
  });

  const tableStore = useTableStore({
    selectedView: tableView,
    onPersist,
    externalFilters: true,
  });

  const endFixedColumns: TableProps<Message>['endFixedColumns'] = useMemo(() => {
    return [
      {
        id: 'options',
        title: '',
        type: 'custom',
        Cell: ({ row }) => <MessageOptionsMenu row={row} />,
      },
    ];
  }, []);

  const countWithMaxPageSize = Math.min(messagesQuery.data?.count ?? 0, pageSize);
  const isLoading = messagesQuery.isLoading || totalCountQuery.isLoading || settingsQuery.isLoading;

  return (
    <Fragment>
      <FilterHeader
        title={t('messages.list.header')}
        filterState={filterState}
        searchPlaceholder={t('messages.header.search.placeholder')}
        isSearchExpanded
      />
      {isLoading ? (
        <Loading />
      ) : (
        <Table
          isRefetching={messagesQuery.isSuccess && messagesQuery.isFetching}
          hideExport
          availableColumns={availableColumns}
          data={messagesQuery.data?.results ?? []}
          idColumn="id"
          store={tableStore}
          endFixedColumns={endFixedColumns}
          isRowHighlighted={(row) => !row.unread}
          searchStr={search}
          emptyState={
            <EmptyState
              icon={<IconMessages />}
              header={t('messages.list.nomessages.header')}
              content={t('messages.list.nomessages.content')}
            />
          }
          filterProps={{
            config: tableFilterConfig,
            addFilterText: t('filters.add'),
            showingText: t('messages.filters.showing', countWithMaxPageSize, totalCount),
          }}
        />
      )}
    </Fragment>
  );
};
